import React, { useMemo } from "react";
import SHA256 from "crypto-js/sha256";
import dayjs from "dayjs";

import {
	CategoryMap,
	ChartGridType,
	ChartStyleConfigLine,
	chartType,
	ChartViewType,
	Entry,
	IFlatChartsCache,
	IFlatChartsCacheItem,
	ISection,
	segmentActiveBorderColors,
	segmentBGcolors,
	segmentBordercolors,
	segmentcolors,
	segmentTextcolors,
	StyleConstantsChart,
} from "../models/interfaces";
import { useChartConfig } from "src/components/Charts/utils/ChartConfigContext";
import { Dispatch } from "redux";
import { updateSelectedStudy } from "src/pages/Studies/states/studiesSlice";
import { setIsDataUpdateModalOpen, setDataUpdateModalMessage } from "src/pages/Insights/states/sectionalSummarySlice";
import { KURAL_CHART_COLOR_PALETTE } from "src/utils/colorPalette";

export function transformDeepDiveSaveJson(data) {
	const transformed = {};

	data.forEach((item) => {
		const { tr_id, ...rest } = item;
		transformed[tr_id] = rest;
	});

	return transformed;
}

// Generates a random 6-digit hex color (e.g. "#A1B2C3") using a CSPRNG
// to satisfy SAST rules around insecure randomness.
export const getRandomColor = (): string => {
	const buf = new Uint8Array(3);
	crypto.getRandomValues(buf);
	const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0"))
		.join("")
		.toUpperCase();
	return `#${hex}`;
};

export const initializeTagColorMap = (tags) => {
	const tagColorMap = {};
	tags?.forEach((tag) => {
		tagColorMap[tag.name] = "";
	});
	return tagColorMap;
};

// Maps a tag name to an assigned display color
interface ITagColorMap {
	[tagName: string]: string;
}

// Safety cap for the random-color fallback loop to prevent runaway iteration
const RANDOM_COLOR_MAX_ATTEMPTS = 50;

// Picks a random element from an array using a CSPRNG to satisfy SAST rules.
// Returns undefined for empty arrays.
const pickSecureRandom = <T,>(arr: readonly T[]): T | undefined => {
	if (arr.length === 0) return undefined;
	const buf = new Uint32Array(1);
	crypto.getRandomValues(buf);
	return arr[buf[0] % arr.length];
};

// Assigns a color to `tagName` inside `tagColorMap` if one isn't already assigned.
// Prefers the sequential color at `tagIndex`, then an unused entry from `colors`,
// and finally falls back to a freshly generated random color.
// NOTE: mutates `tagColorMap` and `usedColors` in place; the return is the same `tagColorMap` reference.
export const assignColorToTagNotes = (
	tagName: string,
	tagColorMap: ITagColorMap,
	usedColors: string[],
	colors: string[] | undefined,
	tagIndex: number,
): ITagColorMap => {
	if (tagColorMap[tagName]) {
		return tagColorMap;
	}

	const paletteIsArray = Array.isArray(colors);
	const usedColorsSet = new Set(usedColors);

	// Sequential assignment from the palette when a slot is available
	if (paletteIsArray && tagIndex < colors.length) {
		const sequentialColor = colors[tagIndex];
		tagColorMap[tagName] = sequentialColor;
		if (!usedColorsSet.has(sequentialColor)) {
			usedColors.push(sequentialColor);
		}
		return tagColorMap;
	}

	// Try to reuse an unused entry from the provided palette
	const availableColors = paletteIsArray ? colors.filter((color) => !usedColorsSet.has(color)) : [];
	let chosenColor = pickSecureRandom(availableColors);

	// Fallback: generate fresh random hex colors until one is unused, capped for safety
	if (!chosenColor) {
		for (let attempt = 0; attempt < RANDOM_COLOR_MAX_ATTEMPTS; attempt++) {
			const candidate = getRandomColor();
			if (!usedColorsSet.has(candidate)) {
				chosenColor = candidate;
				break;
			}
		}
		// Last-resort fallback if every attempt collided (extremely unlikely with 16.7M space)
		chosenColor = chosenColor ?? getRandomColor();
	}

	tagColorMap[tagName] = chosenColor;
	usedColors.push(chosenColor);
	return tagColorMap;
};

export const assignColorToTag = (
	tagName: string,
	tagColorMap: ITagColorMap,
	usedColors: string[],
	colors: string[] | undefined,
	tagIndex: number,
): ITagColorMap => {
	// Return early if tag already has a color
	if (tagColorMap[tagName]) {
		return { [tagName]: tagColorMap[tagName] };
	}

	const paletteIsArray = Array.isArray(colors);
	const usedColorsSet = new Set(usedColors);
	let assignedColor: string | undefined;

	if (paletteIsArray && tagIndex < colors.length) {
		// Sequential assignment from the palette when a slot is available
		assignedColor = colors[tagIndex];
	} else {
		// Try to reuse an unused entry from the provided palette
		const availableColors = paletteIsArray ? colors.filter((color) => !usedColorsSet.has(color)) : [];
		assignedColor = pickSecureRandom(availableColors);

		// Fallback: generate fresh random hex colors until one is unused, capped for safety
		if (!assignedColor) {
			for (let attempt = 0; attempt < RANDOM_COLOR_MAX_ATTEMPTS; attempt++) {
				const candidate = getRandomColor();
				if (!usedColorsSet.has(candidate)) {
					assignedColor = candidate;
					break;
				}
			}
			assignedColor = assignedColor ?? getRandomColor();
		}
	}

	// Return only the new assignment instead of mutating the input
	return { [tagName]: assignedColor };
};

const defaultColors = [
	"#FF6384", // Red
	"#36A2EB", // Blue
	"#FFCE56", // Yellow
	"#4BC0C0", // Teal
	"#9966FF", // Purple
	"#FF9F40", // Orange
];

// New function to get color from palette with opacity
const getRandomColorGeneration = (index) => {
	const paletteLength = colorPallate.length;
	const baseColorIndex = index % paletteLength;
	const cycleCount = Math.floor(index / paletteLength);

	// Get the base color
	const baseColor = colorPallate[baseColorIndex];

	// If it's the first cycle, return the original color
	if (cycleCount === 0) {
		return baseColor;
	}

	// For subsequent cycles, add opacity
	// Start with 10% opacity for first cycle, then 20%, etc.
	const opacity = Math.min(0.9, cycleCount * 0.1); // Cap at 90% opacity

	// Convert hex to RGB and add opacity
	return addOpacityToHexColor(baseColor, opacity);
};

// Helper function to convert hex color to rgba with opacity
const addOpacityToHexColor = (hex, opacity) => {
	// Remove the # if present
	hex = hex.replace("#", "");

	// Parse the hex values to RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Calculate the new RGB values with opacity
	const adjustedR = Math.round(r + (255 - r) * opacity);
	const adjustedG = Math.round(g + (255 - g) * opacity);
	const adjustedB = Math.round(b + (255 - b) * opacity);

	// Convert back to hex
	return `#${adjustedR.toString(16).padStart(2, "0")}${adjustedG.toString(16).padStart(2, "0")}${adjustedB.toString(16).padStart(2, "0")}`;
};

export const colorPallate = KURAL_CHART_COLOR_PALETTE;

export const transformSectionalSummaryData = (payload) => {
	if (!payload) {
		return;
	}

	//const colorPallate = ["#76C2AC", "#4B83DC", "#9681E2", "#9939F3", "#EA39F3", "#2EB8BA", "#809C41", "#65D9FF", "#714EAD", "#65249C", "#862778", "#C089E7", "#1F7197", "#56B0DA"];
	const transformedResponse = payload?.map((section) => {
		const chartData = section?.data?.map((item) => {
			const transformedItem = { name: item?.xAxis };
			item?.series?.forEach((series) => {
				transformedItem[series?.name] = series?.value === "Invalid Field" || series?.value === "No data" || series?.value === "#DIV/0!" ? null : series?.value;
			});
			return transformedItem;
		});

		const tableData = section?.data?.map((item) => {
			const transformedItem = { name: item?.xAxis };
			item?.series?.forEach((series) => {
				transformedItem[series?.name] = series?.value;
				//transformedItem[series.n_size] = series.n_size;
			});
			return transformedItem;
		});

		const chartDataTooltip = section?.data?.map((item) => {
			const transformedItem = { name: item?.xAxis };
			item?.series?.forEach((series) => {
				if (typeof series.n_size === "object" && series.n_size !== null) {
					const min = series.n_size.min ?? 0;
					const max = series.n_size.max ?? 0;
					if (min === max) {
						return `n = ${min}`;
					}
					transformedItem[series.name] = `n = ${min} - ${max}`;
					//return `${min} - ${max}`;
					return `n = ${min} - ${max}`;
				} else if (series.n_size !== undefined && series.n_size !== null) {
					transformedItem[series?.name] = `n = ${series?.n_size}`;

					//return `${item.n_size}`;
					return `n = ${series?.n_size}`;
				} else {
					transformedItem[series?.name] = "n = 0";
					//return "0";
					return "n = 0";
				}
			});
			return transformedItem;
		});

		const n_size_value = section?.data?.map((item) => {
			if (typeof item?.n_size === "object" && item?.n_size !== null) {
				const min = item?.n_size.min ?? 0;
				const max = item?.n_size.max ?? 0;
				if (min === max) {
					return `n = ${min}`;
				}
				//return `${min} - ${max}`;
				return `n = ${min} - ${max}`;
			} else if (item?.n_size !== undefined && item?.n_size !== null) {
				//return `${item?.n_size}`;
				return `n = ${item?.n_size}`;
			} else {
				//return "0";
				return "n = 0";
			}
		});

		const givenColors = { ...section?.chart_config?.ser_color };
		const colorLegends = {};
		const seriesOrder = [];

		// Collect series order from section.data
		section?.data?.forEach((entry) => {
			entry?.series?.forEach((se) => {
				if (!seriesOrder?.includes(se.name)) {
					seriesOrder.push(se.name);
				}
			});
		});

		// Assign colors by index or from givenColors
		seriesOrder?.forEach((name, index) => {
			if (givenColors[name]) {
				colorLegends[name] = givenColors[name];
			} else {
				colorLegends[name] = colorPallate[index] || getRandomColorGeneration(index);
			}
		});

		const colors = seriesOrder?.map((name) => colorLegends[name]);

		return {
			chartTitle: section?.chart_title,
			chartType: section?.chart_type,
			oeData: section?.oeData,
			xAxisValues: section?.x_axis_values,
			yAxisValues: section?.y_axis_values,
			xAxisLables: section?.chart_config?.x_lbl || "",
			yAxisLables: section?.chart_config?.y_lbl || "",
			chartData: chartData || [],
			tableData: tableData || [],
			seriesOrder,
			isLoading: section?.isLoading,
			chartDataTooltip,
			position: section?.position,
			format: section?.format,
			footnote: section?.footnote,
			graph_customization: section?.chart_config,
			colors,
			colorLegends,
			n_size_value,
			question: section?.question || "",
			question_title: section?.question_title || "",
			questions_count: section?.question_count,
			name: section?.name || "",
			analysis_type: section?.analysis_type,
			error: section?.error,
			is_grid: section?.is_grid || false,
		};
	});
	return transformedResponse;
};

export const transformChartItems = (analysisData) => {
	return (
		analysisData?.charts?.map((chart) => {
			const overall = chart?.data?.find((s) => s.name === "Overall");
			return { ...chart, data: overall?.chart_data };
		}) || []
	);
};

export const transformSectionalSummaryData2 = (section: ISection | undefined): any | undefined => {
	if (!section) {
		return;
	}
	// const colorPallate = ["#76C2AC", "#4B83DC", "#9681E2", "#9939F3", "#EA39F3", "#2EB8BA", "#809C41", "#65D9FF", "#714EAD", "#65249C", "#862778", "#C089E7", "#1F7197", "#56B0DA"];

	// Apply logic to extract "Overall" chart data
	const overallData = section?.data?.find((s) => s?.name === "Overall") || section?.data;
	const chartData = (overallData?.chart_data || overallData)?.map((item) => {
		const transformedItem = { name: item?.xAxis };
		item?.series?.forEach((series) => {
			transformedItem[series?.name] = series?.value === "Invalid Field" || series?.value === "No data" || series?.value === "#DIV/0!" ? null : series?.value;
		});
		return transformedItem;
	});

	const tableData = (overallData?.chart_data || overallData)?.map((item) => {
		const transformedItem = { name: item?.xAxis };
		item?.series?.forEach((series) => {
			transformedItem[series?.name] = series?.value;
		});
		return transformedItem;
	});

	const chartDataTooltip = section?.data?.map((item) => {
		const transformedItem = { name: item?.xAxis };
		item?.series?.forEach((series) => {
			if (typeof series?.n_size === "object" && series.n_size !== null) {
				const min = series?.n_size.min ?? 0;
				const max = series?.n_size.max ?? 0;
				if (min === max) {
					return `n = ${min}`;
				}
				transformedItem[series.name] = `n = ${min} - ${max}`;
				//return `${min} - ${max}`;
				return `n = ${min} - ${max}`;
			} else if (series?.n_size !== undefined && series?.n_size !== null) {
				transformedItem[series?.name] = `n = ${series?.n_size}`;

				//return `${item.n_size}`;
				return `n = ${series?.n_size}`;
			} else {
				transformedItem[series?.name] = "n = 0";
				//return "0";
				return "n = 0";
			}
		});
		return transformedItem;
	});

	const n_size_value = (overallData?.chart_data || overallData)?.map((item) => {
		if (typeof item?.n_size === "object" && item?.n_size !== null) {
			const min = item?.n_size.min ?? 0;
			const max = item?.n_size.max ?? 0;
			if (min === max) {
				return `n = ${min}`;
			}
			return `n = ${min} - ${max}`;
		} else if (item?.n_size !== undefined && item?.n_size !== null) {
			return `n = ${item?.n_size}`;
		} else {
			return "n = 0";
		}
	});

	const givenColors = { ...section?.chart_config?.ser_color };
	const colorLegends = {};
	const seriesOrder = [];

	// Collect series order from overallData.chart_data
	(overallData?.chart_data || overallData)?.forEach((entry) => {
		entry?.series?.forEach((se) => {
			if (!seriesOrder?.includes(se.name)) {
				seriesOrder.push(se.name);
			}
		});
	});

	// Assign colors by index or from givenColors
	seriesOrder?.forEach((name, index) => {
		if (givenColors[name]) {
			colorLegends[name] = givenColors[name];
		} else {
			colorLegends[name] = colorPallate[index] || getRandomColorGeneration(index);
		}
	});

	const colors = seriesOrder?.map((name) => colorLegends[name]);

	return {
		chartTitle: section?.chart_title,
		chartType: section?.chart_type,
		oeData: section?.oe_data,
		xAxisValues: section?.x_axis_values,
		yAxisValues: section?.y_axis_values,
		xAxisLables: section?.chart_config?.x_lbl || "",
		yAxisLables: section?.chart_config?.y_lbl || "",
		chartData,
		tableData: tableData || [],
		seriesOrder,
		chartDataTooltip,
		position: section?.position,
		format: section?.format,
		footnote: section?.footnote,
		graph_customization: section?.chart_config,
		colors,
		colorLegends,
		n_size_value,
		question: section?.question || "",
		question_title: section?.question_title || "",
		questions_count: section?.question_count,
		name: section?.name || "",
		analysis_type: section?.analysis_type,
		is_grid: section?.is_grid || false,
		is_weight: section?.is_weight || false,
		value_params: section?.value_params || [],
		error: section?.error,
		axis_idf: section?.axis_idf,
	};
};

export const transformSectionalSummaryData3 = (chart: any): any | undefined => {
	if (!chart) {
		return;
	}

	const overallData = chart.data.find((s) => s.name === "Overall");
	const chartData = overallData.chart_data.map((item) => {
		const transformedItem = { xAxis: item.xAxis };
		item.series.forEach((series) => {
			transformedItem[series.name] = series.value;
		});
		return transformedItem;
	});

	const n_size_value = overallData.chart_data.map((item) => {
		return `n = ${item.n_size}`;
	});

	const colorLegends = {};
	const seriesOrder = [];

	// Collect series order from overallData.chart_data
	overallData.chart_data[0].series?.forEach((se) => {
		if (!seriesOrder.includes(se.name)) {
			seriesOrder.push(se.name);
		}
	});

	// Assign colors
	seriesOrder?.forEach((name, index) => {
		colorLegends[name] = colorPallate[index] || getRandomColorGeneration(index);
	});

	const colors = seriesOrder.map((name) => colorLegends[name]);

	return {
		chartTitle: chart.chart_title,
		chartType: chart.chart_type,
		xAxisValues: [chart.question_number],
		yAxisValues: overallData.chart_data.map((item) => item.xAxisOriginal),
		chartData,
		colors,
		colorLegends,
		n_size_value,
		question: chart.footnote.question_text || "",
		analysis_type: chart.analysis_type,
		is_grid: chart.is_grid || false,
	};
};

export const transformSectionalChartData = (section) => {
	if (!section) return null;

	const chartData = section.data.map((item) => {
		const transformedItem = { name: item.xAxis };
		item?.series?.forEach((series) => {
			transformedItem[series.name] = series.value;
		});
		return transformedItem;
	});

	// const colors = section.study_config.map((config) => config.color);
	// Setup colors with fallback to default colors
	const colors = section?.study_config?.map((config, index) => config.color || defaultColors[index % defaultColors.length]);
	const colorLegends = section?.study_config?.reduce((acc, config) => {
		acc[config.name] = config?.color;
		return acc;
	}, {});

	return {
		chartData,
		colors,
		colorLegends,
		position: section.position,
		chartTitle: section.chart_title,
		n_size_value: section.n_size_value,
		footnote: section,
		graph_customization: section.chart_config,
		xAxisValues: section.x_axis_values,
		yAxisValues: section.y_axis_values,
		chartType: section.chart_type,
	};
};

export function transformSectionalSummaryConfig(
	layout: string,
	graph_customization: any,
	isMiniChart?: boolean,
	isExecutive?: boolean,
	isSecHome?: boolean,
	colorLegendsLength?: number,
	isCompareMode?: boolean,
	customConfig?: any,
) {
	// Create a new config object to avoid mutating the original one
	const newConfig: any = {};
	const executiveHeight = 450;
	const barSize = isCompareMode ? 40 : 80;
	const chartHeight = 440;
	const secHomeHeight =
		isSecHome && graph_customization?.legend_pos === "Top" && colorLegendsLength > 1 ? customConfig?.height?.sec_home_legend_pos : customConfig?.height?.sec_home;
	const customBarSize = customConfig?.bar_size ? customConfig?.bar_size?.size : 40;

	// General transformation logic based on layout
	if (layout === chartType.Stacked_Bar) {
		// newConfig.dataKeyXAxis = "name";
		// newConfig.XAxisType = "number";
		// newConfig.XAxisHide = false;
		// newConfig.dataKeyYAxis = "name";
		// newConfig.YAxisType = "category";
		// newConfig.layout = "vertical";
		// newConfig.YAxisHide = true;
		// newConfig.showLabels = isMiniChart ? false : graph_customization?.data_label;
		// newConfig.showTooltip = isMiniChart ? false : true;
		// newConfig.stackId = "a";
		// newConfig.barSize = isMiniChart ? 20 : barSize;
		// newConfig.height = isExecutive ? executiveHeight : isSecHome ? secHomeHeight : chartHeight;
		// newConfig.xOrientation = "top";
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = isMiniChart ? customConfig?.show_labels : (graph_customization?.data_label ?? customConfig?.show_labels);
		newConfig.showTooltip = customConfig?.show_tooltip;
		newConfig.stackId = customConfig?.stack_id;
		newConfig.width = customConfig?.width;
		newConfig.height = isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Stacked_Column) {
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = isMiniChart ? customConfig?.show_labels : (graph_customization?.data_label ?? customConfig?.show_labels);
		newConfig.showTooltip = customConfig?.show_tooltip;
		newConfig.stackId = customConfig?.stack_id;
		newConfig.width = customConfig?.width;
		newConfig.height = isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Clustered_Column) {
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = graph_customization?.data_label ?? customConfig?.show_labels;
		newConfig.showTooltip = customConfig?.show_tooltip;
		newConfig.width = customConfig?.width;
		newConfig.height = isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Clustered_Bar || layout === chartType.Bar) {
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = graph_customization?.data_label ?? customConfig?.show_labels;
		newConfig.showTooltip = customConfig?.show_tooltip;
		//newConfig.stackId = "a";
		newConfig.width = customConfig?.width;
		newConfig.height = isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Simple_Bar) {
		newConfig.type = "simplebar";
		newConfig.showCartesianGrid = false;
		newConfig.showLegends = false;
		newConfig.dataKeyXAxis = "name";
		newConfig.XAxisType = "category";
		newConfig.XAxisHide = false;
		newConfig.YAxisType = "number";
		newConfig.YAxisHide = true;
		newConfig.layout = "horizontal";
		newConfig.barSize = isMiniChart ? 20 : barSize;
		newConfig.showLabels = isMiniChart ? false : graph_customization?.data_label;
		newConfig.showTooltip = isMiniChart ? false : true;
		newConfig.width = "100%";
		newConfig.height = isExecutive ? executiveHeight : isSecHome ? secHomeHeight : chartHeight;
		newConfig.xOrientation = "top";
	} else if (layout === chartType.Line_Horizontal) {
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = isMiniChart ? customConfig?.show_labels : (graph_customization?.data_label ?? customConfig?.show_labels);
		newConfig.showTooltip = customConfig?.show_tooltip;
		newConfig.width = customConfig?.width;
		newConfig.height = isMiniChart ? customConfig?.height?.chart : isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Line_Vertical) {
		newConfig.showCartesianGrid = customConfig?.show_cartesian_grid;
		newConfig.showLegends = customConfig?.show_legends;
		newConfig.dataKeyXAxis = customConfig?.data_key_x_axis;
		newConfig.XAxisType = customConfig?.x_axis_type;
		newConfig.XAxisHide = customConfig?.x_axis_hide;
		newConfig.YAxisType = customConfig?.y_axis_type;
		newConfig.YAxisHide = customConfig?.y_axis_hide;
		newConfig.layout = customConfig?.layout;
		newConfig.barSize = customBarSize;
		newConfig.showLabels = isMiniChart ? customConfig?.show_labels : (graph_customization?.data_label ?? customConfig?.show_labels);
		newConfig.showTooltip = customConfig?.show_tooltip;
		newConfig.width = customConfig?.width;
		newConfig.height = isMiniChart ? customConfig?.height?.chart : isExecutive ? customConfig?.height?.executive : isSecHome ? secHomeHeight + 10 : customConfig?.height?.chart;
		newConfig.xOrientation = customConfig?.x_orientation;
	} else if (layout === chartType.Metrics) {
		newConfig.showCartesianGrid = false;
		newConfig.showLegends = false;
		newConfig.dataKeyXAxis = "name";
		newConfig.XAxisType = "category";
		newConfig.XAxisHide = false;
		newConfig.YAxisType = "number";
		newConfig.YAxisHide = true;
		newConfig.layout = "vertical";
		newConfig.barSize = 50;
		newConfig.showLabels = isMiniChart ? false : graph_customization?.data_label;
		newConfig.showTooltip = isMiniChart ? false : true;
		newConfig.width = "100%";
		newConfig.height = isExecutive ? "auto" : isSecHome ? secHomeHeight : chartHeight;
		newConfig.xOrientation = "top";
	} else if (layout === chartType.Human_Chart) {
		newConfig.showCartesianGrid = false;
		newConfig.showLegends = false;
		newConfig.dataKeyXAxis = "name";
		newConfig.XAxisType = "category";
		newConfig.XAxisHide = false;
		newConfig.YAxisType = "number";
		newConfig.YAxisHide = true;
		newConfig.layout = "vertical";
		newConfig.barSize = 50;
		newConfig.showLabels = isMiniChart ? false : graph_customization?.data_label;
		newConfig.showTooltip = isMiniChart ? false : true;
		newConfig.width = "100%";
		newConfig.height = isExecutive ? "auto" : isSecHome ? secHomeHeight : chartHeight;
		newConfig.xOrientation = "top";
	}

	const obj = {
		...newConfig,
		dlabel_color: graph_customization?.dlabel_color,
		dlabel_fmt: graph_customization?.dlabel_fmt,
		dlabel_pos: graph_customization?.dlabel_pos?.toLowerCase(),
		dlabel_size: graph_customization?.dlabel_size,
		h_grid: graph_customization?.h_grid,
		legend_pos: graph_customization?.legend_pos,
		line_chart_color: graph_customization?.lc_color,
		line_chart_line: graph_customization?.lc_line,
		lc_marker_sp: graph_customization?.lc_marker_sp,
		line_marker_size: graph_customization?.lc_marker_size,
		ser_fill: graph_customization?.ser_fill,
		show_dlabels: graph_customization?.show_dlabels,
		show_legend: graph_customization?.show_legend,
		show_n_size_in_legend: graph_customization?.show_n_size_in_legend,
		v_grid: graph_customization?.v_grid,
		x_axis: graph_customization?.x_axis,
		y_axis: graph_customization?.y_axis,
	};
	return obj;
}

export function summarizeNSizes(arr) {
	const values = Array.isArray(arr) && arr?.map((item) => parseInt(item?.split("=")?.[1]?.trim()));
	const min = Math.min(...values);
	const max = Math.max(...values);

	if (min === max) {
		return `(n = ${min})`;
	} else {
		return `(n = ${min} - ${max})`;
	}
}

export function hasValueLessForNSize(data?: Record<string, string>[]): boolean {
	try {
		if (!Array.isArray(data) || data.length === 0) return false;

		return data.some((obj) => {
			return Object.entries(obj).some(([key, value]) => {
				if (key === "name") return false;

				if (typeof value !== "string") return false;

				const numbers = value.match(/\d+/g)?.map(Number) || [];

				return numbers.some((num) => num < 30);
			});
		});
	} catch (error) {
		console.error("Error in hasValueLessForNSize:", error);
		return false;
	}
}

export function renderNValueLabel(value, label_color = "#5578b0", label_color_dark = "#FF000099") {
	if (!value || typeof value !== "string") return null;

	const regex = /n\s*=\s*(\d+)(?:\s*-\s*(\d+))?/i;
	const match = value.match(regex);

	if (!match) return null;

	const n1 = parseInt(match[1], 10);
	const n2 = match[2] ? parseInt(match[2], 10) : null;

	const isRed = (n) => !isNaN(n) && n < 30;

	const firstColor = isRed(n1) ? label_color_dark : label_color;
	const secondColor = n2 !== null ? (isRed(n2) ? label_color_dark : label_color) : null;

	return {
		n1,
		n2,
		labelParts: [
			{ text: "n = ", color: label_color },
			{ text: `${n1}`, color: firstColor },
			...(n2 !== null
				? [
					{ text: " - ", color: label_color },
					{ text: `${n2}`, color: secondColor },
				]
				: []),
		],
	};
}

export const getTextWidth = (str, fontSize = 11, fontFamily = "Open Sans") => {
	const canvas = document.createElement("canvas");
	const context = canvas.getContext("2d");
	context.font = `${fontSize}px ${fontFamily}`;
	return context.measureText(str).width;
};

export const STYLE_CONSTANTS_CHART: StyleConstantsChart = {
	yAxisLabelSize: 11,
	yAxisLabelFontWeight: 400,
	yAxisLabelColor: "#5578b0", //"#5578b0";"#112E65"//#112E65//#4d628c//#718fbf//#5578B0
	maxBoxWidth: 80,
	padding: 16,
};

export const chartStyleConfigLine: ChartStyleConfigLine = {
	marginLeftMini: -40,
	yAxisLabelOffset: 0,
	activeDotSize: 4,
	yAxisLabelSize: 11,
	yAxisLabelFontWeight: 400,
	yAxisLabelColor: "#5578b0", // Other options: "#112E65", "#4d628c", "#718fbf"
};

// export const generateSplitByPayload = (spilt_by, selectedItems) => {
// 	const output = {
// 		segments: [
// 			{
// 				segment_name: "Overall",
// 				id: null,
// 				sub_segment: [],
// 			},
// 		],
// 		metadata: [],
// 	};

// 	const isSelected = (val) => selectedItems.includes(val);

// 	const custom = spilt_by?.find((s) => s.key === "custom");
// 	const rest = spilt_by?.filter((s) => s.key !== "custom");

// 	// Helper to traverse and match selected items, excluding top-level title
// 	const traverse = (node, path = []) => {
// 		if (node?.children) {
// 			node.children.forEach((child) => traverse(child, [...path, node.title]));
// 		} else if (isSelected(node?.title)) {
// 			// Skip top-most level from path when creating label
// 			const labelPath = path.length > 1 ? path.slice(1).join("___") : path[0] || "";
// 			output.metadata.push({
// 				label: `${labelPath}___${node.title}`,
// 				is_question: !!node.is_question,
// 				is_numerical: !!node.is_numerical,
// 			});
// 		}
// 	};

// 	// Handle custom segments recursively
// 	custom?.children?.forEach((parent) => {
// 		const matchingSubSegments = [];

// 		const collectSubSegments = (node) => {
// 			if (node?.children) {
// 				node.children.forEach(collectSubSegments);
// 			} else if (isSelected(node?.title)) {
// 				matchingSubSegments.push({
// 					sub_segment_name: node.title,
// 					id: node._id,
// 				});
// 			}
// 		};

// 		collectSubSegments(parent);

// 		if (matchingSubSegments.length) {
// 			output.segments.push({
// 				segment_name: parent?.title,
// 				id: parent?._id,
// 				sub_segment: matchingSubSegments,
// 			});
// 		}
// 	})

// 	// Handle rest (e.g. metadata/questions) recursively
// 	rest.forEach((section) => {
// 		section.children.forEach((item) => traverse(item, [section.title]));
// 	});

// 	return output;
// };

// export const generateSplitByPayload = (split_by, selectedItems) => {
// 	const output = {
// 		segments: [
// 			{
// 				segment_name: "Overall",
// 				id: null,
// 				sub_segment: [],
// 			},
// 		],
// 		metadata: [],
// 	};

// 	const isSelected = (val) => selectedItems.includes(val);

// 	const custom = split_by?.find((s) => s.key === "custom");
// 	const rest = split_by?.filter((s) => s.key.toLowerCase() !== "custom");

// 	// Recursive traversal for metadata (excluding first segment in label)
// 	const traverse = (node, path = []) => {
// 		const currentPath = [...path, node.title];

// 		if (node.children?.length) {
// 			node.children.forEach((child) => traverse(child, currentPath));
// 		} else {
// 			const fullPath = currentPath.join("___");
// 			if (isSelected(fullPath)) {
// 				// Exclude first node (like "Questions") from the label
// 				const label = currentPath.slice(1).join("___");
// 				output.metadata.push({
// 					label,
// 					is_question: !!node.is_question,
// 					is_numerical: !!node.is_numerical,
// 				});
// 			}
// 		}
// 	};

// 	// Handle custom segment logic
// 	custom?.children?.forEach((parent) => {
// 		const matchingSubSegments = [];

// 		const collectSubSegments = (node, path = []) => {
// 			const currentPath = [...path, node.title];

// 			if (node.children?.length) {
// 				node.children.forEach((child) => collectSubSegments(child, currentPath));
// 			} else {
// 				const fullPath = currentPath.join("___");
// 				if (isSelected(fullPath)) {
// 					matchingSubSegments.push({
// 						sub_segment_name: node.title,
// 						id: node._id,
// 					});
// 				}
// 			}
// 		};

// 		collectSubSegments(parent);

// 		if (matchingSubSegments.length) {
// 			output.segments.push({
// 				segment_name: parent?.title,
// 				id: parent?._id,
// 				sub_segment: matchingSubSegments,
// 			});
// 		}
// 	});

// 	// Run for all other split_by sections
// 	rest.forEach((section) => {
// 		section.children.forEach((child) => traverse(child, [section.title]));
// 	});

// 	return output;
// };

// export const generateSplitByPayload = (split_by, selectedItems) => {
// 	const output = {
// 		segments: [
// 			{
// 				segment_name: "Overall",
// 				id: null,
// 				sub_segment: [],
// 			},
// 		],
// 		metadata: [],
// 	};

// 	const isSelected = (val) => selectedItems.includes(val);

// 	const custom = split_by?.find((s) => s.key.toLowerCase() === "custom");
// 	const rest = split_by?.filter((s) => s.key.toLowerCase() !== "custom");

// 	// ---------- Handle non-custom metadata ----------
// 	const traverse = (node, path = []) => {
// 		const currentPath = [...path, node.title];

// 		if (node.children?.length) {
// 			node.children.forEach((child) => traverse(child, currentPath));
// 		} else {
// 			const fullPath = currentPath.join("___");
// 			if (isSelected(fullPath)) {
// 				const label = currentPath.slice(1).join("___");
// 				output.metadata.push({
// 					label,
// 					is_question: !!node.is_question,
// 					is_numerical: !!node.is_numerical,
// 				});
// 			}
// 		}
// 	};

// 	// ---------- Fix: Handle custom segments properly ----------
// 	custom?.children?.forEach((segment) => {
// 		const sub_seg = [];

// 		segment.children?.forEach((sub) => {
// 			const fullPath = `custom___${segment.title}___${sub.title}`;
// 			if (isSelected(fullPath)) {
// 				sub_seg.push({
// 					sub_segment_name: sub.title,
// 					id: sub._id,
// 				});
// 			}
// 		});

// 		if (sub_seg.length) {
// 			output.segments.push({
// 				segment_name: segment.title,
// 				id: segment._id,
// 				sub_segment: sub_seg,
// 			});
// 		}
// 	});

// 	// ---------- Handle other metadata ----------
// 	rest?.forEach((section) => {
// 		section.children.forEach((child) => traverse(child, [section.title]));
// 	});

// 	return output;
// };

export const generateSplitByPayload = (split_by, selectedItems) => {
	if (!selectedItems || selectedItems.length === 0) return {};

	let split_by_field = null;
	let split_values = [];
	let is_numerical = false;
	let is_custom_segment = false;
	let is_question = false;

	const isSelected = (val) => selectedItems.includes(val);

	const rest = split_by?.filter((s) => s.key.toLowerCase() !== "custom");
	const custom = split_by?.find((s) => s.key.toLowerCase() === "custom");
	const questions = split_by?.find((s) => s.key.toLowerCase() === "questions");

	const traverse = (node, path = [], isCustom = false, isQuestions = false) => {
		// For custom items, use id instead of title for leaf nodes only
		const nodeValue = isCustom && !node.children?.length ? node.id : node.key;
		const currentPath = [...path, node.key]; // Always use title for path construction
		const fullPathPrefix = isCustom ? "custom" : isQuestions ? "Questions" : path[0];
		const fullPath = [fullPathPrefix, ...currentPath.slice(1)].join("____");

		if (node.children?.length) {
			node.children.forEach((child) => traverse(child, currentPath, isCustom, isQuestions));
		} else {
			// This is a leaf node
			if (isSelected(fullPath)) {
				if (isCustom) {
					// For custom: path[1] is the segment title, we need its id
					const segmentTitle = path[1]; // "Segment 01"
					const segmentNode = custom?.children?.find((child) => child.key === segmentTitle);
					is_custom_segment = true;

					if (!split_by_field && segmentNode) {
						split_by_field = segmentNode.id; // Use segment ID as field
					}

					// Add the leaf node's id to split_values
					if (segmentNode && split_by_field === segmentNode.id) {
						split_values.push(node.id);
						is_numerical = node.is_numerical ?? false;
					}
				} else if (isQuestions) {
					// Handle questions logic dynamically
					is_question = true;
					is_custom_segment = false;

					// Parse the fullPath to get dynamic parts
					// fullPath format: "Questions___part2___part3___part4___...___leafValue"
					const pathParts = fullPath.split("____");
					// Build the field using values from each level (excluding Questions and leaf value)
					const fieldValueParts = [];

					// Start from the question level (skip "Questions")
					let currentNode = questions?.children?.find((child) => child.key === pathParts[1]);

					if (currentNode) {
						// Add the question ID as the first part
						fieldValueParts.push(pathParts[1]); // This is the question ID like "b45x1"

						// Traverse through the remaining path levels to get values
						for (let i = 2; i < pathParts.length - 1; i++) {
							const levelTitle = pathParts[i];
							const levelNode = currentNode?.children?.find((child) => child.key === levelTitle);

							if (levelNode && levelNode.value) {
								fieldValueParts.push(levelNode.value);
								currentNode = levelNode; // Move to next level
							}
						}
					}

					if (!split_by_field && fieldValueParts.length > 0) {
						// Build the field dynamically from all value parts
						split_by_field = fieldValueParts.join("___");
					}

					// Add the leaf node's title to split_values
					split_values.push(node.title);
					is_numerical = node.is_numerical ?? false;
				} else {
					// Regular logic for non-custom, non-question items
					const field = path[1];
					is_custom_segment = false;
					is_question = false;

					if (!split_by_field) {
						split_by_field = field;
						const parentItem = rest.find((item) => item.key === field)?.children.find((item) => item.key === field);
						is_numerical = parentItem?.is_numerical ?? false;
					}
					if (split_by_field === field) {
						split_values.push(node.title);
					}
				}
			}
		}
	};

	// Process regular sections (excluding custom and questions)
	rest?.filter((section) => section.key.toLowerCase() !== "questions").forEach((section) => {
		section.children.forEach((child) => traverse(child, [section.key], false, false));
	});

	// Process custom section
	if (custom) {
		custom.children.forEach((customChild) => {
			traverse(customChild, [custom.key], true, false);
		});
	}

	// Process questions section
	if (questions) {
		questions.children.forEach((questionChild) => {
			traverse(questionChild, [questions.key], false, true);
		});
	}

	// Sort split_values alphabetically
	split_values?.sort((a, b) => a.localeCompare(b));

	return {
		split_by_field,
		split_values,
		is_numerical,
		is_custom_segment,
		is_question,
	};
};

export const transformDataFilter = (checkedKeys, mainArray, isExport = false) => {
	// Helper to find an item by key in the nested structure
	const findItemByKey = (array, targetKey) => {
		for (const item of array) {
			if (item.key === targetKey) return item;
			if (item.children) {
				const found = findItemByKey(item.children, targetKey);
				if (found) return found;
			}
		}
		return null;
	};

	// Group by filter_field
	const grouped = {};

	checkedKeys?.forEach((checkedItem) => {
		const parts = checkedItem.split("____"); // e.g., ["Questions", "a30", "Cenicriviroc", "Aware"]
		if (parts.length < 3) return;

		const rootKey = parts[0]; // "Questions", "custom", etc.
		const parentKey = parts[1]; // "a30", "a100_1", etc.
		const leafKey = parts[parts.length - 1]; // Last part: "Aware", "Yes", "No", etc.

		const parentItem = findItemByKey(mainArray, parentKey);
		const leafItem = findItemByKey(mainArray, leafKey);

		if (!parentItem) return;

		const isNumerical = parentItem?.is_numerical ?? false;
		const isQuestion = parentItem?.is_question ?? false;
		const isGrid = parentItem?.is_grid ?? false;

		let filterField;
		let filterValue;
		let is_custom_segment = false;

		// Handle Questions with dynamic path-based logic
		if (rootKey.toLowerCase() === "questions" && isQuestion) {
			// Build filterField dynamically from the path (excluding root and leaf)
			const pathParts = parts.slice(1, -1); // Remove "Questions" and leaf value
			const pathValues = [];

			// Get values for each part in the path
			pathParts.forEach((partKey) => {
				const item = findItemByKey(mainArray, partKey);
				if (item) {
					pathValues.push(item.value || item.key);
				}
			});

			// Construct filterField with parent key and all path values
			if (pathValues.length > 0) {
				filterField = `${parentKey}___${pathValues.slice(1).join("___")}`;
			} else {
				filterField = parentKey;
			}

			filterValue = leafItem ? leafItem.value || leafItem.key : leafKey;
		} else if (rootKey.toLowerCase() === "custom") {
			// Custom logic (existing)
			const thirdItem = findItemByKey(mainArray, parts[2]);
			if (!thirdItem || !leafItem) return;

			filterField = isExport ? parentItem.value : parentItem.id;
			filterValue = isExport ? leafItem.value : leafItem.id;
			is_custom_segment = true;
		} else {
			// Default behavior (existing)
			if (!leafItem) return;

			filterField = parentKey;
			filterValue = leafKey;
		}

		// Group the results
		if (!grouped[filterField]) {
			grouped[filterField] = {
				filter_field: filterField,
				filter_values: [filterValue],
				is_numerical: isNumerical,
				is_question: isQuestion,
				is_custom_segment: is_custom_segment,
			};
		} else {
			// Avoid duplicates
			if (!grouped[filterField].filter_values.includes(filterValue)) {
				grouped[filterField].filter_values.push(filterValue);
			}
		}
	});

	// Sort filter_values alphabetically
	Object.values(grouped).forEach((group) => {
		(group as { filter_values?: string[] }).filter_values?.sort((a, b) => a.localeCompare(b));
	});

	return Object.values(grouped);
};

export function reverseSplitByAndDataFilterOld(
	splitBy: {
		split_by_field: string | null;
		split_values: string[] | null;
		is_numerical: boolean;
		is_custom_segment: boolean;
	},
	filters: Array<{
		filter_field: string;
		filter_values: string[];
		is_numerical: boolean;
	}>,
	masterDropdownSplitBy: any[],
	masterDropdownFilters: any[],
): { splitBy: string[]; filters: string[]; filtersData: any[]; splitByData: any[]; selectedSplitBy: any[] } {
	const checkedKeys: string[] = [];
	const selectedItems: string[] = [];
	const filtersData: any[] = [];
	const splitByData: any[] = [];
	const selectedSplitBy: any[] = [];

	// Helper function to find node and build path
	const findNodeAndBuildPath = (array: any[], targetField: string, targetValue?: string): [any, string[]] | null => {
		for (const section of array) {
			for (const item of section.children || []) {
				if (item.key === targetField || item.value === targetField || item.title === targetField) {
					if (!targetValue) {
						return [item, [section.title, item.title]];
					}
					for (const child of item.children || []) {
						if (child.key === targetValue || child.value === targetValue || child.title === targetValue) {
							return [child, [section.title, item.title, child.title]];
						}
					}
				}
			}
		}
		return null;
	};

	// Process filters
	for (const filter of filters) {
		const result = findNodeAndBuildPath(masterDropdownFilters, filter.filter_field);
		if (result) {
			const [parentNode, parentPath] = result;
			for (const value of filter.filter_values) {
				const leafResult = findNodeAndBuildPath(masterDropdownFilters, filter.filter_field, value);
				if (leafResult) {
					const [leafNode, fullPath] = leafResult;
					checkedKeys.push(fullPath.join("___"));
					filtersData.push(leafNode);
				}
			}
		}
	}

	// Process split-by
	if (splitBy.split_by_field && Array.isArray(splitBy.split_values) && splitBy.split_values.length > 0) {
		const result = findNodeAndBuildPath(masterDropdownSplitBy, splitBy.split_by_field);
		if (result) {
			const [parentNode, parentPath] = result;
			for (const value of splitBy.split_values) {
				const leafResult = findNodeAndBuildPath(masterDropdownSplitBy, splitBy.split_by_field, value);
				if (leafResult) {
					const [leafNode, fullPath] = leafResult;
					selectedItems.push(fullPath.join("___"));
					splitByData.push(leafNode);
					selectedSplitBy.push(leafNode?.value); // Add leaf node to selectedSplitBy
				}
			}
		}
	}

	return { filters: checkedKeys, splitBy: selectedItems, filtersData, splitByData, selectedSplitBy };
}

export function reverseSplitByAndDataFilter(
	splitBy: {
		split_by_field: string | null;
		split_values: string[] | null;
		is_numerical: boolean;
		is_custom_segment: boolean;
		is_question?: boolean;
	},
	filters: Array<{
		filter_field: string;
		filter_values: string[];
		is_numerical: boolean;
		is_custom_segment?: boolean;
		is_question?: boolean;
	}>,
	masterDropdownSplitBy: any[],
	masterDropdownFilters: any[],
): { splitBy: string[]; filters: string[]; filtersData: any[]; splitByData: any[]; selectedSplitBy: any[] } {
	const checkedKeys: string[] = [];
	const selectedItems: string[] = [];
	const filtersData: any[] = [];
	const splitByData: any[] = [];
	const selectedSplitBy: any[] = [];

	// Helper function to find item recursively
	const findItemRecursive = (array: any[], predicate: (item: any) => boolean): any | null => {
		for (const item of array) {
			if (predicate(item)) return item;
			if (item.children) {
				const found = findItemRecursive(item.children, predicate);
				if (found) return found;
			}
		}
		return null;
	};

	// Helper function to find item by key in nested structure
	const findItemByKey = (array: any[], targetKey: string): any | null => {
		for (const item of array) {
			if (item.key === targetKey) return item;
			if (item.children) {
				const found = findItemByKey(item.children, targetKey);
				if (found) return found;
			}
		}
		return null;
	};

	// Process split-by
	if (splitBy.split_by_field && Array.isArray(splitBy.split_values) && splitBy.split_values.length > 0) {
		if (splitBy.is_custom_segment) {
			// Handle custom segments
			const customSection = masterDropdownSplitBy.find((s) => s.key.toLowerCase() === "custom");
			if (customSection) {
				// Find the segment by id
				const segment = customSection.children?.find((child: any) => child.id === splitBy.split_by_field);
				if (segment) {
					// Add each split value
					for (const valueId of splitBy.split_values) {
						const valueItem = segment.children?.find((child: any) => child.id === valueId);
						if (valueItem) {
							const path = ["custom", segment.key, valueItem.key];
							selectedItems.push(path.join("____"));
							splitByData.push(valueItem);
							selectedSplitBy.push(valueItem.value || valueItem.key);
						}
					}
				}
			}
		} else if (splitBy.is_question) {
			// Handle questions with dynamic field format
			const questionsSection = masterDropdownSplitBy.find((s) => s.key.toLowerCase() === "questions");
			if (questionsSection) {
				// Parse the split_by_field which is in format like "b45x1___value1___value2"
				const fieldParts = splitBy.split_by_field.split("___");
				const questionId = fieldParts[0]; // e.g., "b45x1"

				// Find the question node
				const questionNode = questionsSection.children?.find((child: any) => child.key === questionId);
				if (questionNode) {
					// Build the path by traversing through the tree structure
					let currentNode = questionNode;
					let pathKeys = ["Questions", questionId];

					// Navigate through the tree using the values in fieldParts
					for (let i = 1; i < fieldParts.length; i++) {
						const valueToFind = fieldParts[i];
						// Find child node with matching value
						const childNode = currentNode.children?.find((child: any) => child.value === valueToFind || child.key === valueToFind);
						if (childNode) {
							pathKeys.push(childNode.key);
							currentNode = childNode;
						}
					}

					// Add each split value (these are the leaf node titles)
					for (const valueTitle of splitBy.split_values) {
						// Find the leaf node by title
						const valueItem = currentNode.children?.find((child: any) => child.title === valueTitle || child.key === valueTitle);
						if (valueItem) {
							const fullPath = [...pathKeys, valueItem.key];
							selectedItems.push(fullPath.join("____"));
							splitByData.push(valueItem);
							selectedSplitBy.push(valueItem.value || valueItem.title || valueItem.key);
						}
					}
				}
			}
		} else {
			// Handle regular metadata fields
			for (const section of masterDropdownSplitBy) {
				if (section.key.toLowerCase() !== "custom" && section.key.toLowerCase() !== "questions") {
					const field = section.children?.find((child: any) => child.key === splitBy.split_by_field);
					if (field) {
						for (const valueTitle of splitBy.split_values) {
							const valueItem = field.children?.find((child: any) => child.title === valueTitle || child.key === valueTitle);
							if (valueItem) {
								const path = [section.key, field.key, valueItem.key];
								selectedItems.push(path.join("____"));
								splitByData.push(valueItem);
								selectedSplitBy.push(valueItem.title || valueItem.key);
							}
						}
					}
				}
			}
		}
	}

	// Process filters
	for (const filter of filters) {
		if (filter.is_custom_segment) {
			// Handle custom segment filters
			const customSection = masterDropdownFilters.find((s) => s.key.toLowerCase() === "custom");
			if (customSection) {
				// For custom segments, filter_field is the segment id
				const segment = customSection.children?.find((child: any) => child.id === filter.filter_field || child.value === filter.filter_field);
				if (segment) {
					for (const valueId of filter.filter_values) {
						// For custom, filter_values are ids
						const valueItem = segment.children?.find((child: any) => child.id === valueId || child.value === valueId);
						if (valueItem) {
							const path = ["custom", segment.key, valueItem.key];
							checkedKeys.push(path.join("____"));
							filtersData.push(valueItem);
						}
					}
				}
			}
		} else if (filter.is_question) {
			// Handle question filters with dynamic field format
			const questionsSection = masterDropdownFilters.find((s) => s.key.toLowerCase() === "questions");
			if (questionsSection) {
				// Parse filter_field in format "questionId___value1___value2"
				const fieldParts = filter.filter_field.split("___");
				const questionId = fieldParts[0]; // e.g., "a30"

				// Find the question node
				const questionNode = questionsSection.children?.find((child: any) => child.key === questionId);
				if (questionNode) {
					// Build the path by traversing through the tree structure
					let currentNode = questionNode;
					let pathKeys = ["Questions", questionId];

					// Navigate through the tree using the values in fieldParts
					for (let i = 1; i < fieldParts.length; i++) {
						const valueToFind = fieldParts[i];
						// Find child node with matching value
						const childNode = currentNode.children?.find((child: any) => child.value === valueToFind || child.key === valueToFind);
						if (childNode) {
							pathKeys.push(childNode.key);
							currentNode = childNode;
						}
					}

					// Add each filter value
					for (const value of filter.filter_values) {
						// Find the leaf node by value or key
						const valueItem = currentNode.children?.find((child: any) => child.value === value || child.key === value || child.title === value);
						if (valueItem) {
							const fullPath = [...pathKeys, valueItem.key];
							checkedKeys.push(fullPath.join("____"));
							filtersData.push(valueItem);
						}
					}
				}
			}
		} else {
			// Handle regular metadata filters
			for (const section of masterDropdownFilters) {
				if (section.key.toLowerCase() !== "custom" && section.key.toLowerCase() !== "questions") {
					const field = section.children?.find((child: any) => child.key === filter.filter_field);
					if (field) {
						for (const value of filter.filter_values) {
							const valueItem = field.children?.find((child: any) => child.key === value || child.title === value);
							if (valueItem) {
								const path = [section.key, field.key, valueItem.key];
								checkedKeys.push(path.join("____"));
								filtersData.push(valueItem);
							}
						}
					}
				}
			}
		}
	}

	return {
		filters: checkedKeys,
		splitBy: selectedItems,
		filtersData,
		splitByData,
		selectedSplitBy,
	};
}

// export function sortByValuesAndThenByMax(dataArray: Entry[]): Entry[] {
// 	// Step 1: Sort key-value pairs inside each object (excluding `name`)
// 	const sortedInternally = dataArray?.map((entry) => {
// 		const { name, ...rest } = entry;

// 		if (!name && !rest) {
// 			return;
// 		}
// 		const numericEntries = Object.entries(rest).filter(([, value]) => typeof value === "number") as [string, number][];

// 		const sorted = numericEntries?.sort(([, a], [, b]) => b - a);

// 		return {
// 			name,
// 			...Object.fromEntries(sorted),
// 		};
// 	});

// 	// Step 2: Sort entire array by max value in each object (excluding `name`)
// 	return sortedInternally.sort((a, b) => {
// 		const maxA = Math.max(...(Object.values(a).filter((v) => typeof v === "number") as number[]));
// 		const maxB = Math.max(...(Object.values(b).filter((v) => typeof v === "number") as number[]));
// 		return maxB - maxA;
// 	});
// }

export function sortByValuesAndThenByMax(dataArray?: Entry[]): Entry[] {
	if (!Array.isArray(dataArray) || dataArray.length === 0) return [];

	try {
		const sortedInternally = dataArray
			.map((entry) => {
				if (!entry || typeof entry !== "object") return undefined;

				const { name, ...rest } = entry;
				if (!name) return undefined;

				const numericEntries = Object.entries(rest).filter(([, value]) => typeof value === "number") as [string, number][];

				const sorted = numericEntries.sort(([, a], [, b]) => b - a);

				return {
					name,
					...Object.fromEntries(sorted),
				};
			})
			.filter(Boolean); // remove undefined entries safely

		return sortedInternally.sort((a, b) => {
			const maxA = Math.max(...(Object.values(a || {}).filter((v) => typeof v === "number") as number[]), -Infinity);
			const maxB = Math.max(...(Object.values(b || {}).filter((v) => typeof v === "number") as number[]), -Infinity);
			return maxB - maxA;
		});
	} catch (error) {
		console.error("Error in sortByValuesAndThenByMax:", error);
		return [];
	}
}

export const getCategoryLevels = (categoryLabel, payload) => {
	// Find the category in the payload
	const category = payload.find((cat) => cat.key === categoryLabel);
	if (!category) return 0; // Return 0 if category not found

	// Recursive function to calculate depth
	const calculateDepth = (item) => {
		if (!item.values || item.values.length === 0) {
			return 1; // Base level if no further nesting
		}
		// Recursive call for deeper levels
		const maxChildDepth = Math.max(...item.values.map(calculateDepth));
		return 1 + maxChildDepth; // Add 1 for the current level
	};

	// Start calculating depth from the found category and subtract 1 from the result
	return Math.max(0, calculateDepth(category) - 1);
};

// Enhanced recursive function to find the parent of the parent array by target label
export const findParentOfParentArray = (data, targetLabel, parentArray = null, itemObj = null) => {
	for (const item of data) {
		// Check if the item's children contain the target
		if (item.values?.some((child) => child.key === targetLabel.key)) {
			return itemObj; // Return the current parent array or top-level if applicable
		}
		// Recurse if deeper levels exist
		if (item.values?.length > 0) {
			const result = findParentOfParentArray(item.values, targetLabel, data, item);
			if (result) return result; // Return as soon as we find the correct parent
		}
	}
	return null; // Return null if not found
};

export const findNestedValues = (data, label) => {
	for (const item of data) {
		if (item.key === label) return item;
		if (item.values?.length > 0) {
			const nested = findNestedValues(item.values, label);
			if (nested) return nested;
		}
	}
	return null;
};

export const transformChartData = (input) => {
	return {
		chartData: input?.map((item) => ({
			label: transformLabelToKMB(item?.label),
			count: parseInt(item?.count, 10), // Convert count to number
		})),
	};
};

export const transformChartDataWithColor = (input) => {
	return {
		chartData: input?.map((item) => ({
			label: transformLabelToKMB(item?.label),
			count: parseInt(item?.count, 10),
			color: item?.hexColor,
			percentage: item?.percentage,
		})),
	};
};

export const getLastPathElements = (arr) => {
	return arr
		.filter((item) => item) // Remove null, undefined, and empty strings
		.map((item) => {
			const parts = item.split("|||||");
			return parts[parts.length - 1]; // Get the last part
		});
};

export function CategoryfilterByLabel(data, targetLabel, totalLevels) {
	function recursiveSearch(items, currentLevel = 1) {
		return items
			.map((item) => {
				if (currentLevel === totalLevels) {
					return item.key === targetLabel ? null : item;
				}
				let filteredValues = item.values && item.values.length > 0 ? recursiveSearch(item.values, currentLevel + 1) : item.values;
				return { ...item, values: filteredValues.filter((v) => v !== null) };
			})
			.filter((item) => item !== null);
	}

	return recursiveSearch(data);
}

// Transformer function to convert the input to the desired output format
export const transformToNSizeSubSegment = (inputData, subSegment, originalCategoryDetails) => {
	// Filter out filters where selected_bucket is empty or not present
	const validFilters = inputData?.filter(
		(filter) => Array.isArray(filter.selected_bucket) && filter.selected_bucket.length > 0 && filter.selected_bucket.some((bucket) => !bucket.label?.includes("@@@invalid")),
	);

	// Mapping valid filters to transformed output
	const transformedFilters = validFilters.map((filter) => {
		const parentObj = findObjectAndParent(originalCategoryDetails, filter?.key);

		return {
			label: parentObj?.parent?.is_question ? `${parentObj?.parent?.label}___${filter?.key}` : filter?.id ? filter?.id : filter?.label,
			selected_bucket: filter.selected_bucket.filter((bucket) => !bucket.label?.includes("@@@invalid")).map((bucket) => (bucket.id ? bucket.id : bucket.label)),
			is_custom: false,
			is_float: filter?.is_float,
			is_segment: filter?.is_segment,
			is_question: parentObj?.parent?.is_question || false,
		};
	});

	return {
		nsize_sub_segment: {
			name: subSegment?.name,
			filters: transformedFilters,
		},
	};
};

export function findObjectAndParent(data, targetLabel) {
	for (const item of data) {
		if (item.values && item.values.length > 0) {
			for (const child of item.values) {
				if (child.key === targetLabel) {
					return { parent: item, object: child };
				}

				if (child.values && child.values.length > 0) {
					for (const grandChild of child.values) {
						if (grandChild.key === targetLabel) {
							return { parent: child, object: grandChild };
						}

						if (grandChild.values && grandChild.values.length > 0) {
							for (const greatGrandChild of grandChild.values) {
								if (greatGrandChild.key === targetLabel) {
									return { parent: grandChild, object: greatGrandChild };
								}
							}
						}
					}
				}
			}
		}
	}
	return null; // Return null if not found
}

export function addObjectToParent(data, parentObj) {
	const { parent, object } = JSON.parse(JSON.stringify(parentObj));
	return data.map((item) => {
		if (item.key === "Qualifying Question" && Array.isArray(item.values)) {
			return {
				...item,
				values: item.values.map((subItem) => (subItem.key === parent.key && Array.isArray(subItem.values) ? { ...subItem, values: [...subItem.values, object] } : subItem)),
			};
		}
		return item;
	});
}

export function transformSegment(input, categoryDetails) {
	// Step 1: Filter out filters with empty or invalid selected_bucket at the source
	const cleanedSubSegments = input.segment.sub_seg.map((subSegment) => ({
		...subSegment,
		filters: subSegment.filters.filter(
			(filter) =>
				Array.isArray(filter.selected_bucket) && filter.selected_bucket.length > 0 && filter.selected_bucket.some((bucket) => !bucket.label?.includes("@@@invalid")),
		),
	}));

	// Step 2: Gather all label occurrences from cleaned filters
	const labelBucketStatus = {};
	cleanedSubSegments.forEach((subSegment) => {
		subSegment.filters?.forEach((filter) => {
			const label = filter.key;
			if (!labelBucketStatus[label]) {
				labelBucketStatus[label] = true;
			}
		});
	});

	// Step 3: Filter category to only include labels that exist in cleaned filters
	const filteredCategory = input.segment.category.filter((label) => labelBucketStatus[label]);

	// Step 4: Apply transformation
	return {
		study_id: input.study_id,
		segment_id: input?.segment_id || null,
		segment: {
			name: input.segment.name,
			description: input.segment.description || "",
			category: filteredCategory,
			sub_segments: cleanedSubSegments.map((subSegment) => {
				const finalFilters = subSegment.filters
					.filter((filter) => filteredCategory.includes(filter.key))
					.map((filter) => {
						const parentObj = findObjectAndParent(categoryDetails, filter.key);
						return {
							label: parentObj?.parent?.is_question ? `${parentObj?.parent?.label}___${filter?.key}` : filter?.id ? filter?.id : filter?.label,
							key: filter?.key,
							selected_bucket: filter.selected_bucket
								.filter((bucket) => !bucket.label?.includes("@@@invalid"))
								.map((bucket) => (bucket.id ? bucket.id : bucket.label)),
							is_custom: filter.is_custom,
							is_float: filter?.is_float,
							is_segment: filter?.is_segment,
							is_question: parentObj?.parent?.is_question || false,
							is_grid: filter?.is_grid,
						};
					});

				return {
					name: subSegment.name,
					filters: finalFilters,
					n_size: subSegment.n_size || 0,
					dimensions: finalFilters.length,
				};
			}),
		},
	};
}

export const transformSelectedBucket = (data, categoryDetails) => {
	const categoryMap: CategoryMap = {};

	// Recursive function to build the category map
	const buildCategoryMap = (categories, parentLabel = null) => {
		categories.forEach((category) => {
			const label = category.key;
			if (parentLabel) {
				if (!categoryMap[parentLabel]) {
					categoryMap[parentLabel] = {};
				}
				categoryMap[parentLabel][label] = {
					label: label,
					key: category.key,
					id: category?.id ?? null,
					count: parseInt(category.count, 10) || 0,
				};
			} else {
				categoryMap[label] = {};
			}

			if (category.values && category.values.length > 0) {
				buildCategoryMap(category.values, label);
			}
		});
	};

	// Build category map from category details
	buildCategoryMap(categoryDetails);

	// Update selected_bucket values in sub_seg' filters and add bgColor
	data.sub_seg.forEach((subSegment, index) => {
		subSegment.bgColor = segmentcolors[index % segmentcolors.length];
		subSegment.dimensions = subSegment.dim;
		subSegment.n_size = subSegment.nsize;
		subSegment.filters.forEach((filter) => {
			const splittedArr = filter.label?.split("___");
			const label = filter.is_question && !filter.is_grid ? splittedArr[splittedArr.length - 1] : filter?.key;
			filter.label = label;
			if (filter.selected_bucket && categoryMap[label]) {
				filter.selected_bucket = filter.selected_bucket.map((bucket) => {
					const categoryItems = categoryMap[label];

					// Try to find by key directly
					let matchedItem = categoryItems[bucket];

					// If not found, try to find by matching ID
					if (!matchedItem) {
						matchedItem = Object.values(categoryItems).find((item) => (item.id as string) === bucket);
					}

					// Fallback if no match found
					return matchedItem || { label: bucket, count: 0 };
				});
			}
		});
	});

	return data;
};

const findPathAndValue = (details, categoryLabel, currentPath = "") => {
	// Remove spaces around colon for consistent comparison
	const formattedCategoryLabel = categoryLabel?.replace(/\s*:\s*/g, ":");

	for (const detail of details) {
		// Remove spaces from label for comparison
		const formattedLabel = String(detail?.key)?.replace(/\s*:\s*/g, ":");
		const newPath = currentPath ? `${currentPath}|||||${detail?.key}` : detail?.key;

		// ✅ If found, return immediately
		if (formattedLabel === formattedCategoryLabel) {
			return { path: newPath, value: detail };
		}

		if (detail?.values && detail?.values.length > 0) {
			const result = findPathAndValue(detail?.values, formattedCategoryLabel, newPath);
			if (result) {
				return result; // ✅ Stop searching once found
			}
		}
	}
	return null;
};
export const buildPathAndValue = (categories, categoryDetails) => {
	const paths = [];
	const values = [];
	const dimensionsValues = [];

	const findParent = (categoryLabel, details, parent = null) => {
		for (const item of details) {
			if (item.key === categoryLabel) {
				return parent || item; // Return parent if found, otherwise return itself
			}
			if (item.values && item.values.length > 0) {
				const foundParent = findParent(categoryLabel, item.values, item);
				if (foundParent) return foundParent;
			}
		}
		return null;
	};

	categories.forEach((cat) => {
		const result = findPathAndValue(categoryDetails, cat); // Assuming this function finds the correct path & value
		const parentCategory = findParent(cat, categoryDetails);

		if (result) {
			paths.push(result.path);
			values.push(result.value);
			dimensionsValues.push(parentCategory); // Store the parent or the category itself
		} else {
			const categoryItem = categoryDetails.find((item) => item.key === cat) || { label: cat };
			paths.push(categoryItem.key);
			values.push(categoryItem);
			dimensionsValues.push(categoryItem); // If no parent, store the category itself
		}
	});

	return { paths, values, dimensionsValues };
};

export const removeCategories = (categoryDetails, categoryArray) => {
	return categoryDetails
		.map((item) => {
			if (categoryArray.includes(item.key)) {
				return null; // Remove direct matches at level 1
			}

			// Process level 2 categories
			const filteredValues = item.values
				.map((subItem) => {
					if (categoryArray.includes(subItem.key)) {
						return null; // Remove direct matches at level 2
					}

					// Process level 3 categories
					const filteredSubValues = subItem.values.filter((subSubItem) => !categoryArray.includes(subSubItem.key));

					// Keep level 2 items if they still have children left
					return filteredSubValues.length > 0 ? { ...subItem, values: filteredSubValues } : subItem.values.length === 0 ? subItem : null;
				})
				.filter(Boolean);

			// Keep level 1 items if they still have children left
			return filteredValues.length > 0 ? { ...item, values: filteredValues } : item.values.length === 0 ? item : null;
		})
		.filter(Boolean);
};

export const generateSegmentColorMap = (subSegments) => {
	const colorMap = {};

	subSegments.forEach((segment, index) => {
		const colorIndex = index % segmentcolors.length; // Ensures cycling through colors
		colorMap[segment._id] = {
			bgColor: segmentBGcolors[colorIndex],
			containerBgColor: segmentcolors[colorIndex],
			textColor: segmentTextcolors[colorIndex],
			borderColor: segmentBordercolors[colorIndex],
			activeBorderColor: segmentActiveBorderColors[colorIndex],
		};
	});

	return colorMap;
};

export function getSubSegmentNames(segments, segmentDetails) {
	const safeSegments = Array.isArray(segments) ? segments : [];

	return safeSegments.flatMap((segment) => {
		// Ensure segment.sub_seg is also an array
		if (!Array.isArray(segment.sub_segments)) return [];
		if (segmentDetails && segmentDetails._id !== segment.id) {
			return segment.sub_segments.map((sub) => sub.name);
		}
		return [];
	});
}

export function getSegmentNames(segments) {
	const safeSegments = Array.isArray(segments) ? segments : [];
	return safeSegments.map((segment) => segment.name);
}

export const formatValue = (value: number, format: string = "##"): string => {
	// TODO: handle 'Invalid Field' here
	if (value === null || value === undefined || isNaN(Number(value))) return "";

	const decimalPlaces = format.includes(".") ? format.split(".")[1].replace(/[^0-9#]/g, "").length : 0;

	const options: Intl.NumberFormatOptions = {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	};

	const useCompact =
		Math.abs(value) >= 1000000 ||
		format.toLowerCase().includes("k") ||
		format.toLowerCase().includes("m") ||
		format.toLowerCase().includes("t") ||
		format.toLowerCase().includes("compact");

	const MAX_SAFE_COMPACT = 1e21;
	const numericValue = typeof value === "bigint" ? BigInt(value) : Number(value);

	if (!isFinite(Number(numericValue))) {
		return "∞";
	}

	if (Math.abs(Number(numericValue)) > MAX_SAFE_COMPACT) {
		return safeExponentialFormat(value, decimalPlaces);
	}

	if (format.includes("%")) {
		options.style = "percent";
	} else if (useCompact) {
		options.notation = "compact";
		options.compactDisplay = "short";
	}

	const numberFormatter = new Intl.NumberFormat("en-US", options);
	const displayValue = format.includes("%") ? value : Number(value);

	// Remove the hardcoded condition and let NumberFormat handle all values naturally
	const formatted = numberFormatter.format(displayValue);

	return formatted;
};

export const safeExponentialFormat = (value: number | string | bigint, decimalPlaces: number): string => {
	try {
		if (typeof value === "bigint") {
			const str = value.toString();
			const isNegative = str.startsWith("-");
			const absStr = isNegative ? str.slice(1) : str;
			return `${isNegative ? "-" : ""}${absStr[0]}.${absStr.slice(1, decimalPlaces + 1)}e+${absStr.length - 1}`;
		}
		if (typeof value === "string") {
			const num = Number(value);
			if (!isFinite(num)) return "∞";
			return num.toExponential(decimalPlaces);
		}
		return Number(value).toExponential(decimalPlaces);
	} catch {
		return "Value too large";
	}
};

export function getMinMaxForBarCharts(charts, type) {
	const chartStats = charts?.map((chart, chartIndex) => {
		const waveValues: number[] = [];

		if (type === "Stacked_Bar") {
			// For Stacked_Bar: sum Wave 1 + Wave 2 + Wave 3 for each item
			chart?.chartData?.forEach((item) => {
				const total = Object.entries(item)
					.filter(([key]) => key !== "name")
					.reduce((sum, [, value]) => sum + (Number(value) || 0), 0);

				waveValues.push(total);
			});
		} else {
			// Original logic for other types
			chart?.chartData?.forEach((wave) => {
				const values = Object.entries(wave)
					.filter(([key]) => key !== "name")
					.map(([, value]) => Number(value))
					.filter((val) => !isNaN(val));

				if (type === chartType.Stacked_Column) {
					const waveSum = values.reduce((sum, val) => sum + val, 0);
					waveValues.push(waveSum);
				} else {
					waveValues.push(...values);
				}
			});
		}

		const chartMin = Math.min(...waveValues);
		const chartMax = Math.max(...waveValues);

		return {
			chart: `Chart ${chartIndex + 1}`,
			values: waveValues,
			overall: { min: chartMin, max: chartMax },
			format: chart.graph_customization?.dlabel_fmt || "",
		};
	});

	const allValues = chartStats.flatMap((stat) => stat.values.map((v) => Number(Number(v).toFixed(3))));
	const globalMin = Math.min(...allValues);
	const globalMax = Math.max(...allValues);

	const format = charts[0]?.graph_customization?.dlabel_fmt || "";
	const isPercentage = format.includes("%");

	if (isPercentage) {
		let reducedMin = globalMin - globalMin * 0.05;
		let extendedMax = globalMax + globalMax * 0.05;

		let adjustedMin = Math.floor(reducedMin * 100);
		adjustedMin -= adjustedMin % 5;
		if (adjustedMin < 0) adjustedMin = 0;

		let adjustedMax = Math.ceil(extendedMax * 100);
		if (adjustedMax % 5 !== 0) {
			adjustedMax += 5 - (adjustedMax % 5);
		}

		if (globalMax <= 1 && adjustedMax / 100 > 1) {
			adjustedMax = 100;
		}

		return {
			chartStats,
			global: { min: adjustedMin / 100, max: adjustedMax / 100 },
		};
	} else {
		let reducedMin = globalMin - globalMin * 0.05;
		let extendedMax = globalMax + globalMax * 0.05;

		let adjustedMin = Math.floor(reducedMin);
		adjustedMin -= adjustedMin % 5;
		if (adjustedMin < 0) adjustedMin = 0;

		let adjustedMax: number | string = Math.ceil(extendedMax);
		if (typeof adjustedMax === "number" && adjustedMax % 5 !== 0) {
			adjustedMax += 5 - (adjustedMax % 5);
		}

		// If the max value is less than 5, let recharts auto-calculate the max
		if (globalMax < 5) {
			adjustedMax = "auto";
		}

		return {
			chartStats,
			global: { min: adjustedMin, max: adjustedMax },
		};
	}
}

export function getMinMaxForLineCharts(charts) {
	const chartStats = charts?.map((chart, chartIndex) => {
		const waveValues: number[] = [];

		chart?.chartData?.forEach((wave) => {
			const values = Object.entries(wave)
				.filter(([key]) => key !== "name")
				.map(([, value]) => Number(value))
				.filter((val) => !isNaN(val));

			waveValues.push(...values);
		});

		const chartMin = Math.min(...waveValues);
		const chartMax = Math.max(...waveValues);

		return {
			chart: `Chart ${chartIndex + 1}`,
			values: waveValues,
			overall: { min: chartMin, max: chartMax },
			format: chart.graph_customization?.dlabel_fmt || "",
		};
	});

	const allValues = chartStats.flatMap((stat) => stat.values);
	const globalMin = Math.min(...allValues);
	const globalMax = Math.max(...allValues);

	// Assume format based on the first chart
	const format = charts[0]?.graph_customization?.dlabel_fmt || "";
	const isPercentage = format.includes("%");

	if (isPercentage) {
		// Percentage mode (scale to 0–100)
		let reducedMin = globalMin - globalMin * 0.05;
		let extendedMax = globalMax + globalMax * 0.05;

		let adjustedMin = Math.floor(reducedMin * 100);
		adjustedMin -= adjustedMin % 5;
		if (adjustedMin < 0) adjustedMin = 0;

		let adjustedMax = Math.ceil(extendedMax * 100);
		if (adjustedMax % 5 !== 0) {
			adjustedMax += 5 - (adjustedMax % 5);
		}

		// Ensure max does not exceed 100 if original max is <= 100
		if (globalMax <= 1 && adjustedMax / 100 > 1) {
			adjustedMax = 100;
		}

		return {
			chartStats,
			global: { min: adjustedMin / 100, max: adjustedMax / 100 },
		};
	} else {
		// Normal decimal mode
		let reducedMin = globalMin - globalMin * 0.05;
		let extendedMax = globalMax + globalMax * 0.05;

		let adjustedMin = Math.floor(reducedMin);
		adjustedMin -= adjustedMin % 5;
		if (adjustedMin < 0) adjustedMin = 0;

		let adjustedMax: number | string = Math.ceil(extendedMax);
		if (adjustedMax % 5 !== 0) {
			adjustedMax += 5 - (adjustedMax % 5);
		}

		// If the max value is less than 5, let recharts auto-calculate the max
		if (globalMax < 5) {
			adjustedMax = "auto";
		}

		return {
			chartStats,
			global: { min: adjustedMin, max: adjustedMax },
		};
	}
}

export const doesMainChartDataExist = (selectedChartsForExport, sectionName: string, mainChartData: any): boolean => {
	return selectedChartsForExport.some(
		(selectedChart) => selectedChart.section_name === sectionName && selectedChart.chart_id === createUniqueChartIdentifier(sectionName, mainChartData),
	);
};

export const createUniqueChartIdentifier = (sectionName: string, chart: any): string => {
	const title = `${sectionName}__${chart?.chart_title}__${chart?.question_number}`;
	return title;
};

export const transformMenuChartData = (newChartData) => {
	return newChartData?.chart_data?.map((waveData) => {
		const result = { name: waveData.xAxis };
		waveData?.series?.forEach((item) => {
			result[item.name] = item?.value;
		});
		return result;
	});
};

export const tranformChartTooltipData = (newChartData) => {
	const chartDataTooltip = newChartData?.chart_data?.map((item) => {
		const transformedItem = { name: item?.xAxis };
		item?.series?.forEach((series) => {
			if (typeof series?.n_size === "object" && series.n_size !== null) {
				const min = series?.n_size.min ?? 0;
				const max = series?.n_size.max ?? 0;
				if (min === max) {
					return `n = ${min}`;
				}
				transformedItem[series.name] = `n = ${min} - ${max}`;
				//return `${min} - ${max}`;
				return `n = ${min} - ${max}`;
			} else if (series?.n_size !== undefined && series?.n_size !== null) {
				transformedItem[series?.name] = `n = ${series?.n_size}`;

				//return `${item.n_size}`;
				return `n = ${series?.n_size}`;
			} else {
				transformedItem[series?.name] = "n = 0";
				//return "0";
				return "n = 0";
			}
		});
		return transformedItem;
	});

	const n_size_value = (newChartData?.chart_data || newChartData)?.map((item) => {
		if (typeof item?.n_size === "object" && item?.n_size !== null) {
			const min = item?.n_size.min ?? 0;
			const max = item?.n_size.max ?? 0;
			if (min === max) {
				return `n = ${min}`;
			}
			return `n = ${min} - ${max}`;
		} else if (item?.n_size !== undefined && item?.n_size !== null) {
			return `n = ${item?.n_size}`;
		} else {
			return "n = 0";
		}
	});
	return { chartDataTooltip, n_size_value };
};

export const capitalizeFirstChar = (str: string) => {
	if (str === null || str === undefined || str === "") return "";
	return str?.charAt(0).toUpperCase() + str?.slice(1);
};

export const reorderActiveSegments = (activeSegments, selectedSegments) => {
	if (!activeSegments || !selectedSegments) {
		return activeSegments || [];
	}

	// Create a map for quick lookup of selectedSegments with their indices
	const selectedOrder = selectedSegments.reduce((acc, segment, index) => {
		acc[segment] = index;
		return acc;
	}, {});

	// Sort activeSegments based on their order in selectedSegments
	const reorderedSegments = [...activeSegments].sort((a, b) => {
		const orderA = selectedOrder[a] !== undefined ? selectedOrder[a] : Infinity;
		const orderB = selectedOrder[b] !== undefined ? selectedOrder[b] : Infinity;
		return orderA - orderB;
	});

	return reorderedSegments;
};

// In your fetchChartPage function, create dropdown config from the chart response
export const createDropdownConfigFromChart = (chartData: any): { [key: string]: string | string[] } => {
	if (!chartData || !chartData?.dropdowns) {
		return {};
	}

	const dropdowns = chartData?.dropdowns;
	const config = {};

	dropdowns?.forEach((dropdown) => {
		const attributeName = dropdown?.name;
		const defaultValue = dropdown?.default_values;

		if (defaultValue !== undefined && defaultValue !== null) {
			config[attributeName] = defaultValue;
		}
	});

	return config;
};

const normalizeDropdownConfig = (config) => {
	if (!config || typeof config !== "object") return "";

	// Sort keys and normalize values
	const normalized = {};
	Object.keys(config)
		.sort()
		.forEach((key) => {
			const value = config[key];
			if (Array.isArray(value)) {
				normalized[key] = [...value].sort();
			} else {
				normalized[key] = value;
			}
		});

	return JSON.stringify(normalized);
};

export const generateCacheHashKey = ({ studyId, tabName, sectionName, pageNo, dropdownConfig = {}, filters = [], splitBy = {}, masterDropDown = null }) => {
	// Defensive: always use string for studyId and sectionName
	const safeStudyId = typeof studyId === "string" ? studyId : String(studyId ?? "");
	const safeSectionName = typeof sectionName === "string" ? sectionName : String(sectionName ?? "");
	const safeTabName = typeof tabName === "string" ? tabName : String(tabName ?? "");
	const safePageNo = typeof pageNo === "number" ? pageNo : Number(pageNo ?? 0);

	// Defensive: ensure dropdownConfig is an object
	const safeDropdownConfig = dropdownConfig && typeof dropdownConfig === "object" ? dropdownConfig : {};

	// IMPORTANT: Handle both raw arrays and already transformed filters/splitBy
	let transformedFilters = [];
	let transformedSplitBy = {};

	// Check if filters is already transformed (has filter_field property)
	if (Array.isArray(filters) && filters.length > 0 && filters[0].filter_field) {
		// Already transformed
		transformedFilters = filters;
	} else if (Array.isArray(filters) && masterDropDown?.add_filter) {
		// Raw filter array - needs transformation
		try {
			transformedFilters = transformDataFilter(filters, masterDropDown.add_filter);
		} catch (error) {
			console.warn("Filter transformation failed:", error);
			transformedFilters = [];
		}
	}

	// Check if splitBy is already transformed (has split_by_field property)
	if (splitBy && typeof splitBy === "object" && (splitBy as { split_by_field?: string }).split_by_field) {
		// Already transformed
		transformedSplitBy = splitBy;
	} else if (Array.isArray(splitBy) && masterDropDown?.split_by) {
		splitBy: {
		}
		// Raw splitBy array - needs transformation
		try {
			transformedSplitBy = generateSplitByPayload(masterDropDown.split_by, splitBy);
		} catch (error) {
			console.warn("SplitBy transformation failed:", error);
			transformedSplitBy = {};
		}
	}

	// Normalize dropdown config
	let normalizedDropdown;
	try {
		normalizedDropdown = normalizeDropdownConfig(safeDropdownConfig);
	} catch (error) {
		console.warn("Dropdown normalization failed:", error);
		normalizedDropdown = "{}";
	}

	// Normalize filters
	let normalizedFilters;
	try {
		normalizedFilters = (Array.isArray(transformedFilters) ? transformedFilters : [])
			.map((filter) => ({
				filter_field: filter.filter_field || "",
				filter_values: [...(filter.filter_values || [])].sort(),
				is_numerical: !!filter.is_numerical,
			}))
			.sort((a, b) => (a.filter_field || "").localeCompare(b.filter_field || ""));
	} catch (error) {
		console.warn("Filter normalization failed:", error);
		normalizedFilters = [];
	}

	// Normalize splitBy
	let normalizedSplitBy;
	try {
		normalizedSplitBy =
			transformedSplitBy && typeof transformedSplitBy === "object" && Object.keys(transformedSplitBy).length > 0
				? {
					split_by_field: transformedSplitBy.split_by_field || "",
					split_values: [...(transformedSplitBy.split_values || [])].sort(),
					is_numerical: !!transformedSplitBy.is_numerical,
					is_custom_segment: !!transformedSplitBy.is_custom_segment,
				}
				: [];
	} catch (error) {
		console.warn("SplitBy normalization failed:", error);
		normalizedSplitBy = [];
	}

	const hashInput = {
		studyId: safeStudyId,
		tabName: safeTabName,
		sectionName: safeSectionName,
		pageNo: safePageNo,
		dropdownConfig: normalizedDropdown,
		filters: normalizedFilters,
		splitBy: normalizedSplitBy,
	};

	const hashString = JSON.stringify(hashInput);

	// Generate hash
	let hash;
	try {
		hash = SHA256(hashString).toString();
	} catch (error) {
		console.warn("SHA256 generation failed:", error);
		// Fallback to a more reliable hash
		hash = btoa(hashString).replace(/[^a-zA-Z0-9]/g, "");
	}

	return hash;
};

export const getCachedChartDataWithHashKey = (
	cache: IFlatChartsCache,
	{ studyId, tabName, sectionName, currentPage, dropdownConfig = {}, filters = [], splitBy = [], masterDropDown = null },
) => {
	try {
		// Generate hash key for lookup using your raw parameter structure
		const hashKey = generateCacheHashKey({
			studyId,
			tabName,
			sectionName,
			pageNo: currentPage,
			dropdownConfig,
			filters,
			splitBy,
			masterDropDown,
		});

		// Direct lookup
		const cachedItem = cache[hashKey];

		if (!cachedItem || !cachedItem.data || cachedItem.data.length === 0) {
			return { success: false, source: null };
		}

		// Extract charts and AI summaries
		const charts = cachedItem.data.map((item) => {
			const { aiSummary, ...chartData } = item;
			return chartData;
		});

		const aiSummaries = cachedItem.aiSummary || [];

		// Use stored metadata or calculate from data
		const pageSize = cachedItem.data.length;
		const totalPages = cachedItem.metadata?.totalPages || currentPage;
		const totalItems = cachedItem.metadata?.totalItems || cachedItem.data.length;

		return {
			success: true,
			data: {
				charts,
				aiSummaries,
				pagination: {
					current_page: currentPage,
					page_size: pageSize,
					total_items: totalItems,
					total_pages: totalPages,
				},
			},
			source: "cache",
		};
	} catch (error) {
		console.error("Error retrieving cached chart data:", error);
		return { success: false, source: null };
	}
};

export const generateFileName = (payload) => {
	// Try to get the study name from the payload
	let studyName = payload?.study_name || "";

	// If no study name, try to get chart title or section name
	if (!studyName) {
		const sections = payload?.data?.sections;
		if (Array.isArray(sections) && sections.length > 0 && Array.isArray(sections[0]?.charts) && sections[0].charts.length > 0) {
			studyName = sections[0].charts[0]?.chart_title || "";
		}
		// If no chart title, use section name
		if (!studyName && Array.isArray(sections) && sections.length > 0) {
			studyName = sections[0]?.name || "";
		}
	}

	// Clean and format the name
	if (studyName) {
		// Replace illegal characters with underscores
		studyName = studyName.replace(/[<>:/\\|?*"]/g, "_");
		// Replace spaces with underscores
		studyName = studyName.replace(/ /g, "_");
		// Remove any remaining non-alphanumeric characters (except underscores and hyphens)
		studyName = studyName.replace(/[^\w\-_]/g, "");
		// Limit length to 100 characters
		const maxLength = 100;
		studyName = studyName.substring(0, maxLength);
	} else {
		studyName = "Charts";
	}

	// Add date in MMM_DD format
	const date = dayjs().format("MMM_DD");

	return `${studyName}_${date}.pptx`;
};

export const normalizeFilters = (filters) => {
	if (!Array.isArray(filters)) return [];
	return filters
		.map((filter) => ({
			filter_field: filter.filter_field || "",
			filter_values: [...(filter.filter_values || [])].sort(),
			is_numerical: !!filter.is_numerical,
		}))
		.sort((a, b) => (a.filter_field || "").localeCompare(b.filter_field || ""));
};

export const normalizeSplitBy = (splitBy) => {
	// Handle array format (which is what your data uses)
	if (Array.isArray(splitBy)) {
		return {
			type: "array",
			values: [...splitBy].sort(),
		};
	}

	// Handle object format
	if (splitBy && typeof splitBy === "object") {
		return {
			type: "object",
			split_by_field: splitBy.split_by_field || "",
			split_values: [...(splitBy.split_values || [])].sort(),
			is_numerical: !!splitBy.is_numerical,
			is_custom_segment: !!splitBy.is_custom_segment,
		};
	}

	// Handle null/undefined/empty cases
	return {
		type: "empty",
		values: [],
	};
};

export const transformLabelToKMB = (label) => {
	// If label is not a string, convert to string first
	if (typeof label !== "string") {
		label = String(label);
	}

	// Check if the label is purely numeric or a numeric range (contains only digits, dots, dashes, and optional negative signs)
	const isNumericLabel = /^-?\d+(\.\d+)?(-\d+(\.\d+)?)?$/.test(label);

	// If it's NOT a numeric label, return as-is
	if (!isNumericLabel) {
		return label;
	}

	// Handle numeric labels (single values or ranges)
	const parts = label.split("-");

	const formatNumber = (num) => {
		const number = parseFloat(num);
		if (isNaN(number)) return num; // Return original if not a number

		const absNum = Math.abs(number);

		if (absNum >= 1e9) {
			return (number / 1e9).toFixed(2) + "B";
		} else if (absNum >= 1e6) {
			return (number / 1e6).toFixed(2) + "M";
		} else if (absNum >= 1e3) {
			return (number / 1e3).toFixed(2) + "K";
		}
		return number.toString();
	};

	// If only one part, it's a single value
	if (parts.length === 1) {
		return formatNumber(parts[0]);
	}

	// If two parts, it's a range
	if (parts.length === 2) {
		return `${formatNumber(parts[0])}-${formatNumber(parts[1])}`;
	}

	// If more than 2 parts or invalid format, return original
	return label;
};

// Utility function to check if element is at bottom
export const isElementAtBottom = (element, threshold = 50) => {
	if (!element) return false;
	const { scrollTop, scrollHeight, clientHeight } = element;
	return scrollHeight - scrollTop <= clientHeight + threshold;
};

export function capitalizeFirstLetter(text) {
	if (!text) return text; // Handle empty strings
	return text.charAt(0).toUpperCase() + text.slice(1);
}

// escapeHtml is centralized in src/utils/security; re-exported here so existing
// import paths continue to work while sharing a single implementation.
export { escapeHtml } from "src/utils/security";

const DEFAULT_MESSAGE = "Updated Insights are available, please refresh the page to view.";

const CHART_SERVICE_ERROR_PREFIX = "Error from chart generation service: ";

/**
 * Parses the nested JSON error string returned by the chart generation service.
 * The detail field comes as: "Error from chart generation service: {\"detail\":[...]}"
 * Returns a user-friendly message or null if parsing fails.
 */
const parseChartServiceError = (errorDetail: string): string | null => {
	try {
		const jsonStart = errorDetail.indexOf("{");
		if (jsonStart === -1) return null;

		const parsed = JSON.parse(errorDetail.slice(jsonStart));
		const errors = parsed?.detail;

		if (!Array.isArray(errors) || errors.length === 0) return null;

		return errors
			.map((err: any) => err.msg ?? err.message)
			.filter(Boolean)
			.join("; ");
	} catch {
		return null;
	}
};
// Replace the single prefix constant with an array of known service error prefixes
const SERVICE_ERROR_PREFIXES = ["Error from chart generation service: ", "Error from slide scaffold service: "];

/**
 * Parses the nested JSON error string returned by backend services.
 * The detail field comes as: "Error from <service>: {\"detail\":[...]}"
 * Returns a user-friendly message or null if parsing fails.
 */
const parseServiceError = (errorDetail: string): string | null => {
	try {
		const jsonStart = errorDetail.indexOf("{");
		if (jsonStart === -1) return null;

		const parsed = JSON.parse(errorDetail.slice(jsonStart));
		const errors = parsed?.detail;

		if (!Array.isArray(errors) || errors.length === 0) return null;

		return errors
			.map((err: any) => err.msg ?? err.message)
			.filter(Boolean)
			.join("; ");
	} catch {
		return null;
	}
};
export const handleInsightsErrorDetail = (error: any, dispatch: Dispatch): boolean => {
	const errorDetail = error?.response?.data?.detail;
	console.error("error detail message", errorDetail);

	// Case 1: Standard error object with active_insights_id (existing flow)
	if (errorDetail?.active_insights_id) {
		dispatch(updateSelectedStudy({ insights_id: errorDetail.active_insights_id }));
		dispatch(setDataUpdateModalMessage(errorDetail?.message || DEFAULT_MESSAGE));
		dispatch(setIsDataUpdateModalOpen(true));
		return true;
	}

	// Case 2: Service-level 422 validation error (string with nested JSON)
	if (typeof errorDetail === "string") {
		const matchesKnownPrefix = SERVICE_ERROR_PREFIXES.some((prefix) => errorDetail.includes(prefix));
		if (matchesKnownPrefix) {
			const message = parseServiceError(errorDetail);
			if (message) {
				dispatch(setDataUpdateModalMessage(message));
				dispatch(setIsDataUpdateModalOpen(true));
				return true;
			}
		}
	}

	return false;
};
